import React from "react";
import { createRoot } from "react-dom/client";
import SignupApp from "./SignupApp";
import "./../assets/css/login-new.css";
import { applyAuthNewTheme } from "../lib/applyAuthNewTheme";

applyAuthNewTheme();
import { devLog } from '../commons/LogUtils';
import { isProductionEnv } from "../commons/VersionUtil";
import "../engagebay-iframe";
import engagebayBaseAPI from "../api";
import OurDomainScriptModule from "../OurDomainScriptCode";
import { captureExtensionContext } from "../commons/util/activateFlow";

// ── Module-level side effects ─────────────────────────────────────────────────

blockUnsupportedBrowser();
loadGrowSumoScript();
captureExtensionContext();

// ── Bootstrap ─────────────────────────────────────────────────────────────────

/**
 * Fetches signup dependencies from the server, injects analytics scripts,
 * then mounts SignupApp into the page root via createRoot.
 *
 * Why this exists: replaces the jQuery+ReactDOMServer.renderToString hybrid
 * in the old index.tsx. createRoot enables React state and synthetic events
 * inside SignupApp, which the old server-render approach blocked entirely.
 *
 * Caller: module init — runs once when signup.html loads.
 */
async function bootstrap() {
    let deps: { [key: string]: any } = {};

    try {
        const res = await engagebayBaseAPI.get(
            "/rest/api/signup/get-signup-dependencies" + window.location.search
        );
        devLog("signupDeps", res.data);
        deps = res.data || {};

        const prefs = deps.prefs;
        if (isProductionEnv() && prefs) {
            OurDomainScriptModule.injectOurDomainScriptCode(false, prefs.isClient, prefs.resellerID, null);
        }
    } catch (err) {
        devLog("Failed to load signup dependencies", err);
        // Render with empty deps so the page still shows.
    }

    injectAnalyticsScripts();

    const container = document.getElementById('root') || document.body;
    createRoot(container).render(<SignupApp deps={deps} />);
}

bootstrap();

// ── Analytics ─────────────────────────────────────────────────────────────────

/**
 * Injects GTM, Hotjar, and Clarity analytics scripts into the document.
 *
 * Why separate from bootstrap: keeps the async fetch logic in bootstrap()
 * readable and lets analytics fire even if the React mount throws. Previously
 * these were inline inside initializeAllEvents() in the old jQuery hybrid.
 *
 * Called once per page load — do not call more than once.
 */
function injectAnalyticsScripts() {
    // Google Tag Manager
    (function (w: Window & typeof globalThis, d: Document, s: string, l: string, i: string) {
        (w as any)[l] = (w as any)[l] || [];
        (w as any)[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
        const f = d.getElementsByTagName(s)[0];
        const j = d.createElement(s) as HTMLScriptElement;
        const dl = l !== 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
        f?.parentNode?.insertBefore(j, f);
    })(window, document, 'script', 'dataLayer', 'GTM-WPHNBVN');

    // Hotjar
    (function (h: any, o: Document, t: string, j: string) {
        h.hj = h.hj || function (this: unknown, ...args: unknown[]) {
            (h.hj.q = h.hj.q || []).push(args);
        };
        h._hjSettings = { hjid: 6465560, hjsv: 6 };
        const a = o.getElementsByTagName('head')[0];
        const r = o.createElement('script') as HTMLScriptElement;
        r.async = true;
        r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
        a?.appendChild(r);
    })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');

    // Clarity — two tracking IDs as in the original, injected separately
    (['mjh299msgj', 'dirtdj0ts2'] as string[]).forEach((clarityId) => {
        (function (c: any, l: Document, a: string, r: string, i: string) {
            c[a] = c[a] || function (this: unknown, ...args: unknown[]) {
                (c[a].q = c[a].q || []).push(args);
            };
            const t = l.createElement(r) as HTMLScriptElement;
            t.async = true;
            t.src = 'https://www.clarity.ms/tag/' + i;
            const y = l.getElementsByTagName(r)[0];
            y?.parentNode?.insertBefore(t, y);
        })(window, document, 'clarity', 'script', clarityId);
    });
}

// ── Utility functions (no jQuery) ─────────────────────────────────────────────

function getBrowserDetails() {
    try {
        const parser = new (window as any).UAParser();
        return {
            browser: parser.getBrowser().name,
            os: parser.getOS().name,
        };
    } catch {
        return {};
    }
}

/**
 * Shows a blocking modal overlay when the visitor is using Safari on Windows.
 *
 * Why this exists: EngageBay no longer supports Safari/Windows. Blocking the
 * page early — before React mounts — prevents confusing UX from the partially-
 * working CSS/JS behaviour on that browser.
 *
 * Called as a module-level side effect before React mounts.
 */
function blockUnsupportedBrowser() {
    const json = getBrowserDetails() as { browser?: string; os?: string };
    if (json.browser !== 'Safari' || json.os !== 'Windows') return;

    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop fade show';
    overlay.style.cssText = 'height:100%;width:100%;opacity:.8;';
    document.body.appendChild(overlay);

    const modalEl = document.createElement('div');
    modalEl.className = 'modal fade show';
    modalEl.setAttribute('tabindex', '-1');
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.style.cssText = 'padding-right:16px;display:block;';

    const dialog = document.createElement('div');
    dialog.className = 'modal-dialog modal-dialog-centered';
    dialog.style.top = '200px';

    const content = document.createElement('div');
    content.className = 'modal-content';
    content.style.borderRadius = '.7rem';

    const body = document.createElement('div');
    body.className = 'modal-body';

    const title = document.createElement('h5');
    title.className = 'modal-title';
    title.style.cssText = 'font-size:16px;margin-bottom:12px;';

    const icon = document.createElement('span');
    icon.className = 'mdi mdi-alert-triangle';
    icon.style.cssText = 'color:#fbbc05;padding-right:5px;';
    title.appendChild(icon);
    title.appendChild(document.createTextNode(' System Alert'));

    const msg = document.createElement('div');
    msg.style.cssText = 'font-size:13px;color:#777;';
    msg.textContent =
        'We are no longer supporting Safari in Windows, Please switch to Chrome or any another browser to continue using EngageBay.';

    body.appendChild(title);
    body.appendChild(msg);
    content.appendChild(body);
    dialog.appendChild(content);
    modalEl.appendChild(dialog);
    document.body.appendChild(modalEl);
}

/**
 * Injects the GrowSumo affiliate tracking script into the page.
 *
 * Why this exists: GrowSumo must be initialised before the signup success
 * handler calls growsumo.createSignup(). Loading it early — before React mounts
 * — avoids a race where a fast typist submits before the script has loaded.
 *
 * Called as a module-level side effect before React mounts.
 */
function loadGrowSumoScript() {
    const gs = document.createElement('script') as any;
    gs.src = 'https://snippet.growsumo.com/growsumo.min.js';
    gs.type = 'text/javascript';
    gs.async = 'true';
    gs.onload = gs.onreadystatechange = function (this: any) {
        const rs = this.readyState;
        if (rs && rs !== 'complete' && rs !== 'loaded') return;
        try { (window as any).growsumo._initialize('pk_ymL68yNmJctQwpYOvxvPV9NsxqQ8MJIQ'); } catch { }
    };
    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(gs, first);
}